import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ClinicBranding from '../components/ClinicBranding';
import {
  Building2,
  Plus,
  Users,
  UserPlus,
  Shield,
  Stethoscope,
  Eye,
  Trash2,
  ChevronRight,
  Loader2,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const ROLE_LABELS = {
  clinic_admin: 'Admin',
  veterinarian: 'Veterinarian',
  technician: 'Technician'
};

const ROLE_ICONS = {
  clinic_admin: Shield,
  veterinarian: Stethoscope,
  technician: Eye
};

const ClinicManagement = () => {
  const { user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinicDetail, setClinicDetail] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', address: '', phone: '' });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'veterinarian' });
  const [submitting, setSubmitting] = useState(false);

  const fetchClinics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clinics`, { withCredentials: true });
      setClinics(data.clinics || []);
    } catch (error) {
      logger.error('Error fetching clinics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClinics(); }, [fetchClinics]);

  const fetchClinicDetail = async (clinicId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clinics/${clinicId}`, { withCredentials: true });
      setClinicDetail(data);
      setSelectedClinic(clinicId);
    } catch (error) {
      toast.error('Failed to load clinic details');
    }
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/clinics`, createForm, { withCredentials: true });
      toast.success('Clinic created');
      setShowCreateForm(false);
      setCreateForm({ name: '', address: '', phone: '' });
      fetchClinics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create clinic');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/clinics/${selectedClinic}/invite`, inviteForm, { withCredentials: true });
      toast.success(`Invited ${inviteForm.email}`);
      setShowInviteForm(false);
      setInviteForm({ email: '', role: 'veterinarian' });
      fetchClinicDetail(selectedClinic);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/clinics/${selectedClinic}/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchClinicDetail(selectedClinic);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await axios.put(`${BACKEND_URL}/api/clinics/${selectedClinic}/members/${memberId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      fetchClinicDetail(selectedClinic);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clinic-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">Clinic Management</h1>
          <p className="text-[#5C5F5C] mt-1">Manage your clinics and team members</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
          data-testid="create-clinic-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Clinic
        </Button>
      </div>

      {/* Create Clinic Form */}
      {showCreateForm && (
        <Card className="border-[#4B6651] bg-white" data-testid="create-clinic-form">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans']">Create New Clinic</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={handleCreateClinic} className="space-y-4">
              <Input
                placeholder="Clinic name"
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                className="border-[#E5E4E0]"
                data-testid="clinic-name-input"
              />
              <Input
                placeholder="Address (optional)"
                value={createForm.address}
                onChange={(e) => setCreateForm({...createForm, address: e.target.value})}
                className="border-[#E5E4E0]"
                data-testid="clinic-address-input"
              />
              <Input
                placeholder="Phone (optional)"
                value={createForm.phone}
                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                className="border-[#E5E4E0]"
                data-testid="clinic-phone-input"
              />
              <Button type="submit" disabled={submitting} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="submit-clinic-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
                Create Clinic
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clinics List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-[#5C5F5C] uppercase tracking-wider">Your Clinics</h2>
          {clinics.length > 0 ? clinics.map((clinic) => (
            <Card
              key={clinic.id}
              className={`border-[#E5E4E0] bg-white cursor-pointer card-hover ${selectedClinic === clinic.id ? 'border-[#4B6651] ring-1 ring-[#4B6651]' : ''}`}
              onClick={() => fetchClinicDetail(clinic.id)}
              data-testid={`clinic-card-${clinic.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4B6651]/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#4B6651]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2A2C2A]">{clinic.name}</p>
                      <p className="text-xs text-[#5C5F5C]">{clinic.member_count} member{clinic.member_count !== 1 ? 's' : ''} · {ROLE_LABELS[clinic.role]}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5C5F5C]" />
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="border-[#E5E4E0] bg-white">
              <CardContent className="p-8 text-center">
                <Building2 className="w-10 h-10 text-[#5C5F5C] mx-auto mb-3" />
                <p className="text-[#5C5F5C]">No clinics yet</p>
                <p className="text-sm text-[#5C5F5C] mt-1">Create a clinic to start managing your team</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Clinic Detail */}
        <div className="lg:col-span-2">
          {clinicDetail ? (
            <div className="space-y-4">
              <Card className="border-[#E5E4E0] bg-white">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-['Work_Sans']">{clinicDetail.name}</CardTitle>
                    {clinicDetail.address && <p className="text-sm text-[#5C5F5C] mt-1">{clinicDetail.address}</p>}
                  </div>
                  {clinicDetail.my_role === 'clinic_admin' && (
                    <Button
                      onClick={() => setShowInviteForm(!showInviteForm)}
                      className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                      data-testid="invite-member-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Invite Form */}
                  {showInviteForm && clinicDetail.my_role === 'clinic_admin' && (
                    <form onSubmit={handleInvite} className="p-4 bg-[#F8F7F4] rounded-lg mb-4 space-y-3" data-testid="invite-form">
                      <div className="flex gap-3">
                        <Input
                          placeholder="Email address"
                          type="email"
                          value={inviteForm.email}
                          onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                          className="flex-1 border-[#E5E4E0] bg-white"
                          data-testid="invite-email-input"
                        />
                        <Select value={inviteForm.role} onValueChange={(val) => setInviteForm({...inviteForm, role: val})}>
                          <SelectTrigger className="w-40 border-[#E5E4E0] bg-white" data-testid="invite-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="veterinarian">Veterinarian</SelectItem>
                            <SelectItem value="technician">Technician</SelectItem>
                            <SelectItem value="clinic_admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" disabled={submitting} size="sm" className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="send-invite-btn">
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Send Invite
                      </Button>
                    </form>
                  )}

                  {/* Members List */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-[#5C5F5C] uppercase tracking-wider mb-3">
                      <Users className="w-4 h-4 inline mr-1" />
                      Members ({clinicDetail.members?.length || 0})
                    </h3>
                    {clinicDetail.members?.map((member) => {
                      const RoleIcon = ROLE_ICONS[member.role] || Eye;
                      return (
                        <div key={member.user_id} className="flex items-center justify-between p-3 bg-[#F8F7F4] rounded-lg" data-testid={`member-${member.user_id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#4B6651]/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-[#4B6651]">
                                {member.user_name?.charAt(0) || member.user_email?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#2A2C2A]">{member.user_name || member.user_email}</p>
                              <p className="text-xs text-[#5C5F5C]">{member.user_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {clinicDetail.my_role === 'clinic_admin' && member.user_id !== user?.id ? (
                              <>
                                <Select value={member.role} onValueChange={(val) => handleRoleChange(member.user_id, val)}>
                                  <SelectTrigger className="w-32 h-8 text-xs border-[#E5E4E0]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="clinic_admin">Admin</SelectItem>
                                    <SelectItem value="veterinarian">Veterinarian</SelectItem>
                                    <SelectItem value="technician">Technician</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveMember(member.user_id)}
                                  className="text-[#5C5F5C] hover:text-[#D94F4F]"
                                  data-testid={`remove-member-${member.user_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <span className="flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-[#5C5F5C]">
                                <RoleIcon className="w-3 h-3" />
                                {ROLE_LABELS[member.role]}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Branding */}
              <ClinicBranding 
                clinicId={selectedClinic} 
                isAdmin={clinicDetail.my_role === 'clinic_admin'} 
              />
            </div>
          ) : (
            <Card className="border-[#E5E4E0] bg-white">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-[#5C5F5C] mx-auto mb-4" />
                <p className="text-[#5C5F5C]">Select a clinic to view details and manage members</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicManagement;
